import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { User2FACode } from './User2FACode';
import { LicenceSearchItem } from './LicenceSearchItem';
import { ParticipantId } from './ParticipantId';
import type { EntityState } from './../enums/EntityState';
import type { Gender } from './../enums/Gender';
import { formatDateIso } from '@/utilities/dateutils';

export class ParticipantWithLicences extends BaseObj {
    public ID: number;
    public __id: number;

    public Address1: string | null;
    public __address1: string | null;

    public Address2: string | null;
    public __address2: string | null;

    public AddressCountryISO2: string | null;
    public __addresscountryiso2: string | null;

    public readonly Age: number | null;

    public Birthday: Date | null;
    public __birthday: Date | null;

    public City: string | null;
    public __city: string | null;

    public Current2FAState: User2FACode | null;
    public __current2fastate: User2FACode | null;

    public Email: string | null;
    public __email: string | null;

    public EventAge: number | null;
    public __eventage: number | null;

    public EventCount: number;
    public __eventcount: number;

    public Firstname: string | null;
    public __firstname: string | null;

    public Gender: Gender | null;
    public __gender: Gender | null;

    public LastChanged: Date;
    public __lastchanged: Date;

    public LastEvent: string | null;
    public __lastevent: string | null;

    public LastEventClubName: string | null;
    public __lasteventclubname: string | null;

    public LastEventDate: Date | null;
    public __lasteventdate: Date | null;

    public Licences: LicenceSearchItem[];
    public __licences: LicenceSearchItem[];

    public NationalityCountryID: number;
    public __nationalitycountryid: number;

    public ParticipantIDs: ParticipantId[];
    public __participantids: ParticipantId[];

    public Phone: string | null;
    public __phone: string | null;

    public PostalNumber: string | null;
    public __postalnumber: string | null;

    public Surname: string | null;
    public __surname: string | null;

    public Username: string | null;
    public __username: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Address1?: string | null;
        Address2?: string | null;
        AddressCountryISO2?: string | null;
        Age?: number | null;
        Birthday?: Date | null;
        City?: string | null;
        Current2FAState?: User2FACode | null;
        Email?: string | null;
        EntityState?: EntityState;
        EventAge?: number | null;
        EventCount?: number;
        Firstname?: string | null;
        Gender?: Gender | null;
        LastChanged?: Date;
        LastEvent?: string | null;
        LastEventClubName?: string | null;
        LastEventDate?: Date | null;
        Licences?: LicenceSearchItem[];
        NationalityCountryID?: number;
        ParticipantIDs?: ParticipantId[];
        Phone?: string | null;
        PostalNumber?: string | null;
        Surname?: string | null;
        Username?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ParticipantWithLicences>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('ParticipantWithLicences tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Address1 = values.Address1 ?? null;
        this.__address1 = values.Address1 ?? null;
        Object.defineProperty(this, 'Address1', {
            get(): string | null {
                return this.__address1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Address1', this.__address1, value);
                this.__address1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Address2 = values.Address2 ?? null;
        this.__address2 = values.Address2 ?? null;
        Object.defineProperty(this, 'Address2', {
            get(): string | null {
                return this.__address2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Address2', this.__address2, value);
                this.__address2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AddressCountryISO2 = values.AddressCountryISO2 ?? null;
        this.__addresscountryiso2 = values.AddressCountryISO2 ?? null;
        Object.defineProperty(this, 'AddressCountryISO2', {
            get(): string | null {
                return this.__addresscountryiso2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('AddressCountryISO2', this.__addresscountryiso2, value);
                this.__addresscountryiso2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Age = values.Age ?? null;

        this.Birthday = values.Birthday ?? null;
        this.__birthday = values.Birthday ?? null;
        Object.defineProperty(this, 'Birthday', {
            get(): Date | null {
                return this.__birthday;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Birthday', this.__birthday, value);
                this.__birthday = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.City = values.City ?? null;
        this.__city = values.City ?? null;
        Object.defineProperty(this, 'City', {
            get(): string | null {
                return this.__city;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('City', this.__city, value);
                this.__city = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Current2FAState = values.Current2FAState ?? null;
        this.__current2fastate = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
            childObject: values.Current2FAState ?? null,
            childField: 'Current2FAState',
        });
        Object.defineProperty(this, 'Current2FAState', {
            get(): User2FACode | null {
                return this.__current2fastate;
            },
            set(value: User2FACode | null) {
                this.__current2fastate = this.__entityStateHandler.createProxyWrapperWithMutationTracking({
                    childObject: value,
                    childField: 'Current2FAState',
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.Email = values.Email ?? null;
        this.__email = values.Email ?? null;
        Object.defineProperty(this, 'Email', {
            get(): string | null {
                return this.__email;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Email', this.__email, value);
                this.__email = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventAge = values.EventAge ?? null;
        this.__eventage = values.EventAge ?? null;
        Object.defineProperty(this, 'EventAge', {
            get(): number | null {
                return this.__eventage;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EventAge', this.__eventage, value);
                this.__eventage = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventCount = values.EventCount ?? 0;
        this.__eventcount = values.EventCount ?? 0;
        Object.defineProperty(this, 'EventCount', {
            get(): number {
                return this.__eventcount;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventCount', this.__eventcount, value);
                this.__eventcount = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Firstname = values.Firstname ?? null;
        this.__firstname = values.Firstname ?? null;
        Object.defineProperty(this, 'Firstname', {
            get(): string | null {
                return this.__firstname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Firstname', this.__firstname, value);
                this.__firstname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Gender = values.Gender ?? 'm';
        this.__gender = values.Gender ?? 'm';
        Object.defineProperty(this, 'Gender', {
            get(): Gender | null {
                return this.__gender;
            },
            set(value: Gender | null) {
                this.__entityStateHandler.propertyChanged('Gender', this.__gender, value);
                this.__gender = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastChanged = values.LastChanged ?? new Date('1970-01-01T00:00:00.000');
        this.__lastchanged = values.LastChanged ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'LastChanged', {
            get(): Date {
                return this.__lastchanged;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('LastChanged', this.__lastchanged, value);
                this.__lastchanged = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastEvent = values.LastEvent ?? null;
        this.__lastevent = values.LastEvent ?? null;
        Object.defineProperty(this, 'LastEvent', {
            get(): string | null {
                return this.__lastevent;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LastEvent', this.__lastevent, value);
                this.__lastevent = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastEventClubName = values.LastEventClubName ?? null;
        this.__lasteventclubname = values.LastEventClubName ?? null;
        Object.defineProperty(this, 'LastEventClubName', {
            get(): string | null {
                return this.__lasteventclubname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LastEventClubName', this.__lasteventclubname, value);
                this.__lasteventclubname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastEventDate = values.LastEventDate ?? null;
        this.__lasteventdate = values.LastEventDate ?? null;
        Object.defineProperty(this, 'LastEventDate', {
            get(): Date | null {
                return this.__lasteventdate;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('LastEventDate', this.__lasteventdate, value);
                this.__lasteventdate = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Licences = values.Licences ?? [];
        this.__licences = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Licences ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('Licences', newVal),
        });
        this.__licences.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Licences', this.Licences),
            );
        });
        Object.defineProperty(this, 'Licences', {
            get(): LicenceSearchItem[] {
                return this.__licences;
            },
            set(value: LicenceSearchItem[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__licences = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: LicenceSearchItem[], newVal: LicenceSearchItem[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Licences', newVal),
                });
                this.__licences.forEach((child: LicenceSearchItem) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Licences', this.Licences),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Licences', this.Licences);
            },
            enumerable: true,
            configurable: true,
        });

        this.NationalityCountryID = values.NationalityCountryID ?? 0;
        this.__nationalitycountryid = values.NationalityCountryID ?? 0;
        Object.defineProperty(this, 'NationalityCountryID', {
            get(): number {
                return this.__nationalitycountryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NationalityCountryID', this.__nationalitycountryid, value);
                this.__nationalitycountryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParticipantIDs = values.ParticipantIDs ?? [];
        this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.ParticipantIDs ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
        });
        this.__participantids.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
            );
        });
        Object.defineProperty(this, 'ParticipantIDs', {
            get(): ParticipantId[] {
                return this.__participantids;
            },
            set(value: ParticipantId[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: ParticipantId[], newVal: ParticipantId[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
                });
                this.__participantids.forEach((child: ParticipantId) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs);
            },
            enumerable: true,
            configurable: true,
        });

        this.Phone = values.Phone ?? null;
        this.__phone = values.Phone ?? null;
        Object.defineProperty(this, 'Phone', {
            get(): string | null {
                return this.__phone;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Phone', this.__phone, value);
                this.__phone = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PostalNumber = values.PostalNumber ?? null;
        this.__postalnumber = values.PostalNumber ?? null;
        Object.defineProperty(this, 'PostalNumber', {
            get(): string | null {
                return this.__postalnumber;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('PostalNumber', this.__postalnumber, value);
                this.__postalnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Surname = values.Surname ?? null;
        this.__surname = values.Surname ?? null;
        Object.defineProperty(this, 'Surname', {
            get(): string | null {
                return this.__surname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Surname', this.__surname, value);
                this.__surname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Username = values.Username ?? null;
        this.__username = values.Username ?? null;
        Object.defineProperty(this, 'Username', {
            get(): string | null {
                return this.__username;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Username', this.__username, value);
                this.__username = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Address1": ${JSON.stringify(this.Address1)}` +
            ',' +
            `"Address2": ${JSON.stringify(this.Address2)}` +
            ',' +
            `"AddressCountryISO2": ${JSON.stringify(this.AddressCountryISO2)}` +
            ',' +
            `"Age": ${JSON.stringify(this.Age)}` +
            ',' +
            `"Birthday": ${this.Birthday ? '"' + formatDateIso(this.Birthday).substring(0, 10) + '"' : null}` +
            ',' +
            `"City": ${JSON.stringify(this.City)}` +
            ',' +
            `"Current2FAState": ${!this.Current2FAState ? null : this.Current2FAState?.serialize()}` +
            ',' +
            `"Email": ${JSON.stringify(this.Email)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventAge": ${JSON.stringify(this.EventAge)}` +
            ',' +
            `"EventCount": ${JSON.stringify(this.EventCount)}` +
            ',' +
            `"Firstname": ${JSON.stringify(this.Firstname)}` +
            ',' +
            `"Gender": ${this.Gender == null ? null : this.Gender == 'm' ? 0 : this.Gender == 'f' ? 1 : this.Gender == 'n' ? 2 : null}` +
            ',' +
            `"LastChanged": ${'"' + formatDateIso(this.LastChanged) + '"'}` +
            ',' +
            `"LastEvent": ${JSON.stringify(this.LastEvent)}` +
            ',' +
            `"LastEventClubName": ${JSON.stringify(this.LastEventClubName)}` +
            ',' +
            `"LastEventDate": ${this.LastEventDate ? '"' + formatDateIso(this.LastEventDate) + '"' : null}` +
            ',' +
            `"Licences": [ ${this.Licences.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"NationalityCountryID": ${JSON.stringify(this.NationalityCountryID)}` +
            ',' +
            `"ParticipantIDs": [ ${this.ParticipantIDs.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"Phone": ${JSON.stringify(this.Phone)}` +
            ',' +
            `"PostalNumber": ${JSON.stringify(this.PostalNumber)}` +
            ',' +
            `"Surname": ${JSON.stringify(this.Surname)}` +
            ',' +
            `"Username": ${JSON.stringify(this.Username)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ParticipantWithLicences from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Address1?: string | null;
        Address2?: string | null;
        AddressCountryISO2?: string | null;
        Birthday?: Date | null;
        City?: string | null;
        Current2FAState?: User2FACode | null;
        Email?: string | null;
        EventAge?: number | null;
        EventCount?: number;
        Firstname?: string | null;
        Gender?: Gender | null;
        LastChanged?: Date;
        LastEvent?: string | null;
        LastEventClubName?: string | null;
        LastEventDate?: Date | null;
        Licences?: LicenceSearchItem[];
        NationalityCountryID?: number;
        ParticipantIDs?: ParticipantId[];
        Phone?: string | null;
        PostalNumber?: string | null;
        Surname?: string | null;
        Username?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): ParticipantWithLicences {
        return new ParticipantWithLicences(values);
    }

    public static _FromServerJson(json: any): ParticipantWithLicences {
        return new ParticipantWithLicences({
            ID: json.ID,
            Address1: json.Address1 ?? '',
            Address2: json.Address2 ?? '',
            AddressCountryISO2: json.AddressCountryISO2 ?? '',
            Age: json.Age ?? null,
            Birthday: json.Birthday ? (json.Birthday instanceof Date ? json.Birthday : new Date(json.Birthday + ' 00:00:00')) : null,
            City: json.City ?? '',
            Current2FAState: json.Current2FAState ? User2FACode._FromServerJson(json.Current2FAState) : null,
            Email: json.Email ?? '',
            EventAge: json.EventAge ?? null,
            EventCount: json.EventCount,
            Firstname: json.Firstname ?? '',
            Gender: json.Gender == null ? null : json.Gender == 0 ? 'm' : json.Gender == 1 ? 'f' : json.Gender == 2 ? 'n' : null,
            LastChanged: json.LastChanged instanceof Date ? json.LastChanged : new Date(json.LastChanged),
            LastEvent: json.LastEvent ?? '',
            LastEventClubName: json.LastEventClubName ?? '',
            LastEventDate: json.LastEventDate
                ? json.LastEventDate instanceof Date
                    ? json.LastEventDate
                    : new Date(json.LastEventDate)
                : null,
            Licences: json.Licences?.map((x: any) => LicenceSearchItem._FromServerJson(x)),
            NationalityCountryID: json.NationalityCountryID,
            ParticipantIDs: json.ParticipantIDs?.map((x: any) => ParticipantId._FromServerJson(x)),
            Phone: json.Phone ?? '',
            PostalNumber: json.PostalNumber ?? '',
            Surname: json.Surname ?? '',
            Username: json.Username ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ParticipantWithLicences from another instance of ParticipantWithLicences.
     * @param other The ParticipantWithLicences you want to clone
     * @returns A cloned ParticipantWithLicences
     */
    public static FromOther(other: ParticipantWithLicences): ParticipantWithLicences {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ParticipantWithLicences._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ParticipantWithLicences.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Address1'
            | 'Address2'
            | 'AddressCountryISO2'
            | 'Age'
            | 'Birthday'
            | 'City'
            | 'Current2FAState'
            | 'Email'
            | 'EventAge'
            | 'EventCount'
            | 'Firstname'
            | 'Gender'
            | 'LastChanged'
            | 'LastEvent'
            | 'LastEventClubName'
            | 'LastEventDate'
            | 'Licences'
            | 'NationalityCountryID'
            | 'ParticipantIDs'
            | 'Phone'
            | 'PostalNumber'
            | 'Surname'
            | 'Username'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ParticipantWithLicences';
}
